import React, { useState, useEffect } from 'react';
import { Plus, Search, Settings, Edit, Trash2 } from 'lucide-react';
import { 
  Button, 
  SearchBar, 
  LoadingSpinner,
  StatusBadge 
} from '../../components/common';
import { Navigation } from '../../components/layouts';
import { ServiceForm } from '../../components/forms';
import { serviceService } from '../../services/serviceService';

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [error, setError] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    loadServices();
  }, []);

  // Debounce search to avoid too many API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim()) {
        handleSearch();
      } else {
        loadServices();
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const loadServices = async () => {
    try {
      setLoading(true);
      setError(null);
      const servicesData = await serviceService.getAll();
      setServices(servicesData || []);
    } catch (error) {
      console.error('Failed to load services:', error);
      setError('Nu s-au putut încărca serviciile. Verificați conexiunea la server.');
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      loadServices();
      return;
    }

    try {
      setIsSearching(true);
      setError(null);
      const searchResults = await serviceService.search(searchTerm.trim());
      setServices(searchResults || []);
    } catch (error) {
      console.error('Failed to search services:', error);
      setError('Căutarea a eșuat. Încercați din nou.');
      setServices([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleServiceSubmit = async (serviceData) => {
    try {
      setError(null);
      
      if (editingService) {
        // Update existing service
        await serviceService.update(editingService.id, serviceData);
        console.log('Service updated successfully:', editingService.id);
      } else {
        // Create new service
        await serviceService.create(serviceData);
        console.log('Service created successfully');
      }
      
      // Reload services list
      await loadServices();
      setShowServiceForm(false);
      setEditingService(null);
    } catch (error) {
      console.error('Failed to save service:', error);
      setError(editingService ? 'Nu s-a putut actualiza serviciul.' : 'Nu s-a putut crea serviciul.');
    }
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setShowServiceForm(true);
  };

  const handleDeleteService = async (serviceId) => {
    if (window.confirm('Sunteți sigur că doriți să ștergeți acest serviciu?')) {
      try {
        setError(null);
        await serviceService.delete(serviceId);
        console.log('Service deleted successfully:', serviceId);
        
        // Reload services list
        await loadServices();
      } catch (error) {
        console.error('Failed to delete service:', error);
        setError('Nu s-a putut șterge serviciul.');
      }
    }
  };

  // Format price with fallback
  const formatPrice = (price, currency, unit) => {
    try {
      const numPrice = typeof price === 'number' ? price : parseFloat(price) || 0;
      return `${numPrice.toLocaleString('ro-RO')} ${currency || 'RON'}/${unit || 'bucată'}`;
    } catch (error) {
      return `${price || 0} ${currency || 'RON'}/${unit || 'bucată'}`;
    }
  };

  // Get usage count color
  const getUsageCountColor = (count) => {
    const usageCount = count || 0;
    if (usageCount > 10) return 'bg-green-100 text-green-800';
    if (usageCount > 5) return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-800';
  };

  // Format date with fallback
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('ro-RO');
    } catch (error) {
      return 'N/A';
    }
  };

  // Show loading state during initial load
  if (loading && services.length === 0) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" text="Se încarcă serviciile..." />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Navigation
        title="Gestiune Servicii"
        subtitle={`${services.length} servicii în catalogul sistemului`}
        actions={[
          {
            label: 'Serviciu Nou',
            icon: Plus,
            variant: 'primary',
            onClick: () => {
              setEditingService(null);
              setShowServiceForm(true);
            }
          }
        ]}
      />

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="text-sm text-red-700">
              {error}
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <SearchBar
          placeholder="Căutați servicii după nume, descriere sau cod CPV..."
          value={searchTerm}
          onChange={setSearchTerm}
          onClear={() => setSearchTerm('')}
          loading={isSearching}
        />
      </div>

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((service) => (
          <div key={service.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  {service.name || 'Serviciu fără nume'}
                </h3>
                <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                  {service.description || 'Fără descriere'}
                </p>
              </div>
              <div className="flex space-x-2 ml-4">
                <Button
                  size="sm"
                  variant="outline"
                  icon={Edit}
                  onClick={() => handleEditService(service)}
                  title="Editează serviciul"
                />
                <Button
                  size="sm"
                  variant="danger"
                  icon={Trash2}
                  onClick={() => handleDeleteService(service.id)}
                  title="Șterge serviciul"
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Preț:</span>
                <span className="font-semibold text-gray-900">
                  {formatPrice(service.price, service.currency, service.unit)}
                </span>
              </div>

              {service.cpvCode && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Cod CPV:</span>
                  <span className="text-sm font-mono text-gray-700">{service.cpvCode}</span>
                </div>
              )}

              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Utilizări:</span>
                <StatusBadge 
                  status={`${service.usageCount || 0} contracte`}
                  customColors={{
                    [`${service.usageCount || 0} contracte`]: getUsageCountColor(service.usageCount)
                  }}
                />
              </div>

              <div className="pt-3 border-t border-gray-200">
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Creat: {formatDate(service.createdAt)}</span>
                  <span>Actualizat: {formatDate(service.updatedAt)}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {services.length === 0 && !loading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Settings className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nu au fost găsite servicii</h3>
          <p className="text-gray-600 mb-6">
            {searchTerm ? 'Încercați să modificați termenii de căutare.' : 'Începeți prin a adăuga primul serviciu.'}
          </p>
          {!searchTerm && (
            <Button
              icon={Plus}
              onClick={() => {
                setEditingService(null);
                setShowServiceForm(true);
              }}
            >
              Adaugă Primul Serviciu
            </Button>
          )}
        </div>
      )}

      {/* Loading Overlay for Grid */}
      {loading && services.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl">
            <LoadingSpinner size="md" text="Se actualizează serviciile..." />
          </div>
        </div>
      )}

      {/* Service Form Modal */}
      <ServiceForm
        service={editingService}
        isOpen={showServiceForm}
        onClose={() => {
          setShowServiceForm(false);
          setEditingService(null);
        }}
        onSubmit={handleServiceSubmit}
      />
    </div>
  );
};

export default ServicesPage;